// SubjectSummary.jsx
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import * as constants from "../constants";
import Documents from "../services/Documents";
import Fileupload from "./common/FileUpload";
import Assessments from "./Assessments";
import { useLocation, useParams } from "react-router";
import queryString from "query-string";
import {
  BookOpen,
  FileText,
  GraduationCap,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  FolderOpen,
  BadgeCheck,
} from "lucide-react";

// Loading Skeleton - matches ProgramSummary style
const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="bg-white rounded-md p-4 sm:p-6 lg:p-8 border border-gray-200">
      <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
      <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div className="flex-1 min-w-0">
              <div className="h-3 w-16 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
      <div className="h-5 w-32 bg-gray-200 rounded mb-4"></div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: {
      bg: "bg-green-50",
      text: "text-green-700",
      dot: "bg-green-500",
      icon: CheckCircle2,
    },
    "in-progress": {
      bg: "bg-blue-50",
      text: "text-blue-700",
      dot: "bg-blue-500",
      icon: Clock,
    },
    pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      dot: "bg-amber-500",
      icon: AlertCircle,
    },
    active: {
      bg: "bg-green-50",
      text: "text-green-700",
      dot: "bg-green-500",
      icon: CheckCircle2,
    },
  };

  const key = (status || "").toLowerCase().replace(" ", "-");
  const config = statusConfig[key] || statusConfig.pending;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.text} text-sm font-medium rounded-full`}>
      <div className={`w-1.5 h-1.5 ${config.dot} rounded-full`}></div>
      {status}
    </span>
  );
};

const SubjectSummary = () => {
  const { id } = useParams();
  const location = useLocation();
  const qs = useMemo(() => queryString.parse(location.search), [location.search]);
  const initialProgramId = qs?.programId;

  const [loading, setLoading] = useState(true);
  const [hasResources, setHasResources] = useState(false);
  const [programIdState, setProgramIdState] = useState(initialProgramId);

  const [state, setState] = useState({
    buttonList: undefined,
    subjectId: id,
    subjectSummary: undefined,
    resourcesCardHeaderSettings: {
      headingText: "Resources",
      description: "available to download",
      objectType: 22,
    },
    cardHeaderSettings: {
      headingText: "My Assessments",
      description: "To be submitted to Trainer",
    },
  });

  const inferHasResourcesFromSummary = (summary) => {
    const count =
      (summary?.resourceCount ?? 0) ||
      (summary?.resources?.length ?? 0) ||
      (summary?.documents?.length ?? 0) ||
      (summary?.programDocuments?.length ?? 0) ||
      (summary?.subjectDocuments?.length ?? 0);

    if (typeof count === "number") return count > 0;
    return false;
  };

  const checkHasResources = async (objectId, objectType) => {
    const res = await Axios.get(
      `${constants.BASEURL}Document/Objects/${objectId}?objectType=${objectType}`
    );
    return Array.isArray(res.data) && res.data.length > 0;
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);

      try {
        const res = await Axios.get(
          `${constants.BASEURL}Student/SubjectUOSSummary/${id}?objectType=subject&programId=${programIdState ?? ""}`
        );

        if (cancelled) return;

        const subjectSummary = res.data;

        setState((pre) => ({
          ...pre,
          subjectSummary,
          subjectId: id,
        }));

        if (subjectSummary?.enrolmentId && subjectSummary.enrolmentId !== programIdState) {
          setProgramIdState(subjectSummary.enrolmentId);
        }

      let present = false;
      try {
        present = await checkHasResources(id, state.resourcesCardHeaderSettings.objectType);
      } catch {
        const inferred = inferHasResourcesFromSummary(subjectSummary);
        present = Boolean(inferred);}
      setHasResources(Boolean(present));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [id, programIdState, state.resourcesCardHeaderSettings.objectType]);

  const renderDocuments = (documentType) => {
    if (documentType === 1) {
      const options = {
        ...state.resourcesCardHeaderSettings,
        objectId: state?.subjectId,
      };
      return <Documents options={options} />;
    }
    return <Fileupload />;
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  const { subjectSummary } = state;

  return (
    <div className="px-4 py-4">
    <div className="mx-auto w-full space-y-6">
      <div className="bg-white rounded-md border border-gray-200 p-4 sm:p-6 lg:p-8 shadow-md space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">         <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
                <BookOpen className="w-4 h-4" />
                {subjectSummary?.subjectCode || "N/A"}
              </span>
              {subjectSummary?.status && <StatusBadge status={subjectSummary?.status} />}
            </div>

              <h2 className="text-[1.2rem] font-semibold text-gray-900 mb-2 break-words sm:truncate">
              {subjectSummary?.subjectName || "Subject Details"}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Start Date</p>
              <p className="text-sm font-medium text-gray-900 truncate">
                {subjectSummary?.startDate || "Not specified"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">End Date</p>
              <p className="text-sm font-medium text-gray-900">
                {subjectSummary?.endDate || "Not specified"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Type</p>
            <p className="text-sm font-medium text-gray-900 truncate">
              {subjectSummary?.subjectType || "Not specified"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            !subjectSummary?.compulsory ? "bg-rose-50" : "bg-emerald-50"
          }`}
        >
          <BadgeCheck
            className={`w-5 h-5 ${
              !subjectSummary?.compulsory ? "text-rose-600" : "text-emerald-600"
            }`}
          />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Compulsory</p>
          <p className="text-sm font-medium text-gray-900 truncate">
            {subjectSummary?.compulsory ? "Yes" : "No"}
          </p>
        </div>
      </div>
        </div>
      </div>

    <div className="grid grid-cols-1  gap-4 sm:gap-6">
        {state?.subjectId && hasResources && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-md min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Resources</h3>
                <p className="text-xs text-gray-500">Available to download</p>
              </div>
            </div>
              <div className="w-full overflow-x-auto">
            <div className="min-w-[520px]">{renderDocuments(1)}</div>
            </div>
          </div>
        )}

          <div
            className={`bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-md min-w-0 ${
          !hasResources ? "lg:col-span-2" : ""
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">My Assessments</h3>
              <p className="text-xs text-gray-500">Submit to your trainer</p>
            </div>
          </div>

          <Assessments
            cardHeaderSettings={state?.cardHeaderSettings}
            listData={state?.buttonList}
            objectId={id}
            objectType={1}
            programId={programIdState}
          />
      </div>
       </div>
      </div>
    </div>
  );
};

export default SubjectSummary;